'use client'

import Footer from '@components/Footer'
import Nav from '@components/Nav'
import { HeroUIProvider } from "@heroui/react"
import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Script from 'next/script'
import FontCatalogLoader from '@components/fonts/FontCatalogLoader'
import { PlatformTooltipsProvider } from './PlatformTooltips'
import { isPublicPlatformPath as isPublicSitePath } from '@utils/publicPlatformRoutes'

type SessionActionsContextValue = {
    signOut: () => Promise<void>
}

const SessionActionsContext = createContext<SessionActionsContextValue>({
    signOut: async () => {},
})

export const useSessionActions = () => useContext(SessionActionsContext)

export default function HeroUIProviderTemp({
    basicurl, children, props, signoutHandler
}: {
    basicurl: any,
    children: React.ReactNode,
    props: any,
    signoutHandler: Function
}) {
    const pathname = usePathname()
    const router = useRouter()
    const isPanelPath = pathname === '/panel' || pathname.startsWith('/panel/')
    const isPublicPlatformPath = isPublicSitePath(pathname)
    const [isAuthenticated, setIsAuthenticated] = useState(props.isValid)

    useEffect(() => {
        setIsAuthenticated(props.isValid)
    }, [props.isValid])

    useEffect(() => {
        const accessPages = ['/signin', '/signup', '/', '/404', '/signin', '/license', '/policy', '/superadmin', '/yukassaagentslink', '/signinadmin']

        const isProjectInvitePage = pathname.indexOf('/project-invite/') == 0

        if ((!accessPages.includes(pathname) && !isPublicPlatformPath && !isProjectInvitePage) && !isAuthenticated) {
            router.replace('/signin')
            return
        }
        if (isAuthenticated && ['/signin', '/signup', '/signin', '/signinadmin'].includes(pathname)) {
            router.replace('/panel')
            return
        }
    }, [isAuthenticated, pathname, router])

    const handleSignOut = useCallback(async () => {
        try {
            const result: any = await signoutHandler()
            if (result?.success) {
                setIsAuthenticated(false)
            }
        } catch (error) {
            console.error('Не удалось выполнить выход из аккаунта', error)
        } finally {
            router.refresh()
        }
    }, [router, signoutHandler])

    const sessionActions = useMemo(() => ({ signOut: handleSignOut }), [handleSignOut])

    useEffect(() => {

        const sent: Set<string> = new Set()
        const handleError = (event: ErrorEvent) => {
            const stack = event.error?.stack || ''
            const key = `${event.message}${stack}`
            if (sent.has(key)) return
            sent.add(key)
            fetch(`${basicurl}/api/log-client-error`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: event.message,
                    stack,
                    url: window.location.href,
                    userAgent: navigator.userAgent
                })
            }).catch(() => {})
        }
        const handleRejection = (event: PromiseRejectionEvent) => {
            const message = event.reason?.message || 'Unhandled promise rejection'
            const stack = event.reason?.stack || ''
            const key = `${message}${stack}`
            if (sent.has(key)) return
            sent.add(key)
            fetch(`${basicurl}/api/log-client-error`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message,
                    stack,
                    url: window.location.href,
                    userAgent: navigator.userAgent
                })
            }).catch(() => {})
        }

        window.addEventListener('error', handleError)
        window.addEventListener('unhandledrejection', handleRejection)
        return () => {
            window.removeEventListener('error', handleError)
            window.removeEventListener('unhandledrejection', handleRejection)
        }
    }, [])

    return (
        <PlatformTooltipsProvider initialEnabled={props.tooltipsEnabled !== false}>
            <SessionActionsContext.Provider value={sessionActions}>
                <HeroUIProvider className='w-full dark h-full'>
                <FontCatalogLoader />
                {!props.isMobileAdminEmbed && !isPanelPath && <Nav isValid={isAuthenticated} t={props.t} timeLeft={props.timeLeft} signoutHandler={handleSignOut}/>}
                {props.botConfig &&
                    <Script>
                        {`
                            document.documentElement.style.setProperty('--color-app-etc', \`${props.botConfig?.colormap?.[0].color}\`)
                            document.documentElement.style.setProperty('--color-app-main', \`${props.botConfig?.colormap?.[2].color}\`)
                            document.documentElement.style.setProperty('--color-app-fon', \`${props.botConfig?.colormap?.[1].type == 'image' ? `url('${basicurl}/api/image?filename=/download/${props.imgfieldid}/${props.botConfig?.colormap?.[1].variants[props.botConfig?.colormap?.[1].type]}')` : props.botConfig?.colormap?.[1].variants[props.botConfig?.colormap?.[1].type]}\`)
                            document.documentElement.style.setProperty('--color-app-blocks', \`${props.botConfig?.colormap?.[3].color}\`)
                            document.documentElement.style.setProperty('--color-app-textblocks', \`${props.botConfig?.colormap?.[4].color}\`)

                            document.documentElement.style.setProperty('--app-padding-top', \`${props.botConfig?.colormap?.[5].padding.top}px\`)
                            document.documentElement.style.setProperty('--app-padding-right', \`${props.botConfig?.colormap?.[5].padding.right}px\`)
                            document.documentElement.style.setProperty('--app-padding-bottom', \`${props.botConfig?.colormap?.[5].padding.bottom}px\`)
                            document.documentElement.style.setProperty('--app-padding-left', \`${props.botConfig?.colormap?.[5].padding.left}px\`)

                            document.documentElement.style.setProperty('--app-border-radius', \`${props.botConfig?.colormap?.[5].radius.topLeft}px ${props.botConfig?.colormap?.[5].radius.topRight}px ${props.botConfig?.colormap?.[5].radius.bottomRight}px ${props.botConfig?.colormap?.[5].radius.bottomLeft}px\`)

                            document.documentElement.style.setProperty('--app-font-family', \`${props.botConfig?.colormap?.[5].font.family}\`)
                            document.documentElement.style.setProperty('--app-font-size', \`${props.botConfig?.colormap?.[5].font.size}\`)

                            document.documentElement.style.setProperty('--app-gap', \`${props.botConfig?.colormap?.[5].positionelements.gap}px\`)

                            document.documentElement.style.setProperty('--app-border-top', \`${props.botConfig?.colormap?.[5].border.top.width}px ${props.botConfig?.colormap?.[5].border.top.style} ${props.botConfig?.colormap?.[5].border.top.color}\`)
                            document.documentElement.style.setProperty('--app-border-right', \`${props.botConfig?.colormap?.[5].border.right.width}px ${props.botConfig?.colormap?.[5].border.right.style} ${props.botConfig?.colormap?.[5].border.right.color}\`)
                            document.documentElement.style.setProperty('--app-border-bottom', \`${props.botConfig?.colormap?.[5].border.bottom.width}px ${props.botConfig?.colormap?.[5].border.bottom.style} ${props.botConfig?.colormap?.[5].border.bottom.color}\`)
                            document.documentElement.style.setProperty('--app-border-left', \`${props.botConfig?.colormap?.[5].border.left.width}px ${props.botConfig?.colormap?.[5].border.left.style} ${props.botConfig?.colormap?.[5].border.left.color}\`)

                            document.documentElement.style.setProperty('--app-shadow', \`${props.botConfig?.colormap?.[5].shadow.x}px ${props.botConfig?.colormap?.[5].shadow.y}px ${props.botConfig?.colormap?.[5].shadow.blur}px ${props.botConfig?.colormap?.[5].shadow.spread}px ${props.botConfig?.colormap?.[5].shadow.color} ${props.botConfig?.colormap?.[5].shadow.inset == true ? 'inset' : ''}\`)
                            document.documentElement.style.setProperty('--app-component-shadow', \`${props.botConfig?.colormap?.[5].shadowcomponent.x}px ${props.botConfig?.colormap?.[5].shadowcomponent.y}px ${props.botConfig?.colormap?.[5].shadowcomponent.blur}px ${props.botConfig?.colormap?.[5].shadowcomponent.spread}px ${props.botConfig?.colormap?.[5].shadowcomponent.color} ${props.botConfig?.colormap?.[5].shadowcomponent.inset == true ? 'inset' : ''}\`)
                        `}
                    </Script>
                }
                {children}
                {isPublicPlatformPath && <Footer/>}
                </HeroUIProvider>
            </SessionActionsContext.Provider>
        </PlatformTooltipsProvider>
    )
}
